import { api } from '@/lib/api'

/**
 * Fetch the list of Form 11 refund categories supported for the given tax year.
 * Mirrors GET  /api/tax/refund-categories/{year}
 */
export async function fetchRefundCategories(year: number): Promise<string[]> {
  const { data } = await api.get<string[]>(`/tax/refund-categories/${year}`)
  return data
}

/**
 * Ask the backend to calculate the refund amount for a single Form 11 category.
 * Mirrors POST /api/refund-calculate
 *
 * The backend returns: { amount: number }
 * This helper unwraps the response and only returns the numeric amount.
 */
export async function calculateRefund(
  year: number,
  category: string,
  data: Record<string, any> = {},
): Promise<number> {
  try {
    const { data: result } = await api.post<{ amount: number }>(
      '/tax/refund-calculate',
      {
        year,
        category,
        data,
      },
    )

    // Defensive – make sure we always return a number.
    const amount = Number(result?.amount)
    return Number.isFinite(amount) ? amount : 0
  } catch (error) {
    console.error('Refund calculation failed', { year, category, error })
    return 0
  }
}

// ---------------------------------------------------------------------------
// New bulk-mode helper -------------------------------------------------------
// ---------------------------------------------------------------------------

/**
 * Calculate refund amounts for many categories in one request.
 * Mirrors the new bulk format accepted by POST /api/refund-calculate.
 *
 * The backend expects:
 *   { year, items: [ { category, data? }, … ] }
 * and returns:
 *   { year, amounts: { [category]: number } }
 *
 * This helper unwraps the response and returns the amounts map.
 * If anything goes wrong it throws, letting callers decide whether to fall back
 * to the single-category helper.
 */
export async function calculateRefundBulk(
  year: number,
  items: Array<{ category: string; data?: Record<string, any> }>,
): Promise<number[]> {
  const { data } = await api.post<
    { year: number; amounts: number[] }
  >('/tax/refund-calculate', {
    year,
    items,
  })

  // The backend may respond with either a record (legacy) or an array that
  // preserves the order of the `items` list (supports duplicates). Detect
  // which shape we received and return accordingly.
  if (Array.isArray((data as any)?.amounts)) {
    return (data as { amounts: number[] }).amounts
  }

  return (data as { amounts: number[] }).amounts ?? []
} 